import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import Pipeline from "@/pages/Pipeline";
import Settings from "@/pages/Settings";
import Scraper from "@/pages/Scraper";
import Analytics from "@/pages/Analytics";
import Duplicates from "@/pages/Duplicates";
import AgentActivity from "@/pages/AgentActivity";
import GrantResearcher from "@/pages/GrantResearcher";
import Writer from "@/pages/Writer";
import SplashPage from "@/pages/SplashPage";
import AuthCallback from "@/pages/AuthCallback";
import UserManagement from "@/pages/UserManagement";
import WhiteLabelSettings from "@/pages/WhiteLabelSettings";
import { Loader2 } from "lucide-react";

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { user, loading, token } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#264653] via-[#1a4a4f] to-[#1a3a42] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#e9c46a] mx-auto mb-4" />
          <p className="text-[#faf3e0] text-lg font-mono">Loading...</p>
        </div>
      </div>
    );
  }

  // Check both user state and token in localStorage
  const hasToken = token || localStorage.getItem('access_token');
  
  if (!user && !hasToken) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public route wrapper (redirects to dashboard if already logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#264653] via-[#1a4a4f] to-[#1a3a42] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#e9c46a] mx-auto mb-4" />
          <p className="text-[#faf3e0] text-lg font-mono">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute>
          <SplashPage />
        </PublicRoute>
      } />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="leads" element={<Leads />} />
        <Route path="pipeline" element={<Pipeline />} />
        <Route path="scraper" element={<Scraper />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="duplicates" element={<Duplicates />} />
        <Route path="agents" element={<AgentActivity />} />
        <Route path="grant-researcher" element={<GrantResearcher />} />
        <Route path="writer" element={<Writer />} />
        <Route path="settings" element={<Settings />} />
        <Route path="admin/users" element={<UserManagement />} />
        <Route path="admin/whitelabel" element={<WhiteLabelSettings />} />
      </Route>

      {/* Catch all - redirect to login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
